import { useMutation, useQueryClient, type UseMutationOptions, type UseMutationResult } from 'react-query';

export interface EntityMutationConfig<TData, TVariables> {
  mutationFn: (variables: TVariables) => Promise<TData>;
  invalidateKeys?: string[];
  options?: Omit<UseMutationOptions<TData, unknown, TVariables>, 'mutationFn'>;
}

/**
 * Factory that creates a typed useMutation wrapper.
 * Automatically invalidates the specified query keys on success.
 *
 * Returns a hook function: (overrides?) => UseMutationResult
 */
export function createEntityMutation<TData, TVariables>(
  config: EntityMutationConfig<TData, TVariables>
) {
  return function useEntityMutation(
    overrides?: Omit<UseMutationOptions<TData, unknown, TVariables>, 'mutationFn'>
  ): UseMutationResult<TData, unknown, TVariables> {
    const queryClient = useQueryClient();

    return useMutation(config.mutationFn, {
      ...config.options,
      ...overrides,
      onSuccess: (data, variables, context) => {
        if (config.invalidateKeys) {
          config.invalidateKeys.forEach((key) => {
            void queryClient.invalidateQueries(key);
          });
        }
        config.options?.onSuccess?.(data, variables, context);
        overrides?.onSuccess?.(data, variables, context);
      },
    });
  };
}
